import merge from 'lodash/merge'
import Qs from 'qs' // dependency of nuxtjs / body-parser

/**
 * Prepare url to use to request API, replacing tokens in it.
 * @param {String} url the url to call.
 * @param {Object} tokens the tokens to insert into the url.
 * @returns {String} the url to use.
 */
export const prepareUrl = (url, tokens) => {
  if (tokens) {
    return Object.entries(tokens).reduce(
      (acc, [key, value]) => acc.replace(`{${key}}`, value),
      url
    )
  }

  return url
}

export const DEFAULT_REQUEST_PARAMS = Object.freeze({
  api: 'main', // the api to request (main, chat, club, see nuxt.config.js)
})

/**
 * Execute a request to a remote API.
 * @param {Object} action the store action.
 * @param {String} api the api to use.(main, chat, club, see nuxt.config.js)
 * @param {Object} options some options to execute the request. See DEFAULT_REQUEST_PARAMS for available options.
 * @returns {Object} an object to use to query the remote api.
 * @see DEFAULT_REQUEST_PARAMS
 */
export const request = (action, api = 'main', options = {}) => {
  const { $axios, $config } = action
  const allOptions = merge(DEFAULT_REQUEST_PARAMS, options)

  const baseUrl = $config.apiUrl[api || allOptions.api]

  if (!baseUrl) {
    const errMessage = `No URL defined for api '${api}'`
    console.warn(errMessage)
    throw new URIError(errMessage)
  }

  return {
    get(endpoint, urlTokens, queryParams) {
      const url = prepareUrl(endpoint, urlTokens)
      return $axios.get(url, {
        baseURL: baseUrl,
        params: queryParams,
        paramsSerializer: (params) =>
          /**
           * Encode `teamId: [1, 2]` to `team_id[]=1&team_id[]=2` in query string.
           * By default, axios (through Qs) converts `teamId: [1, 2]` into `team_id=1,2`
           * @see https://github.com/ljharb/qs?tab=readme-ov-file#stringifying
           */
          Qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }),
      })
    },
    post(endpoint, urlTokens, data) {
      const url = prepareUrl(endpoint, urlTokens)
      return $axios.post(url, data, { baseURL: baseUrl })
    },
    patch(endpoint, urlTokens, data) {
      const url = prepareUrl(endpoint, urlTokens)
      return $axios.patch(url, data, { baseURL: baseUrl })
    },
    put(endpoint, urlTokens, data) {
      const url = prepareUrl(endpoint, urlTokens)
      return $axios.put(url, data, { baseURL: baseUrl })
    },
    delete(endpoint, urlTokens, queryParams) {
      const url = prepareUrl(endpoint, urlTokens)
      return $axios.delete(url, { baseURL: baseUrl, params: queryParams })
    },
  }
}
